/** Group MediaItems into chronological buckets for the timeline view. */
import type { MediaItem } from './types.ts';

export interface TimelineBucket {
  /** Stable key, e.g. '2024-06'. */
  key: string;
  /** Display label, e.g. 'June 2024'. */
  label: string;
  /** Sort value (epoch-ms of the bucket start) for ordering buckets. */
  sortValue: number;
  items: MediaItem[];
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/**
 * Group items by month (year-month). Items without a timestamp are collected
 * into a trailing "Undated" bucket. Buckets are returned newest-first.
 */
export function groupByMonth(items: MediaItem[]): TimelineBucket[] {
  const buckets = new Map<string, TimelineBucket>();
  const undated: MediaItem[] = [];

  for (const item of items) {
    if (item.timestamp == null) {
      undated.push(item);
      continue;
    }
    const d = new Date(item.timestamp);
    const year = d.getFullYear();
    const month = d.getMonth();
    const key = `${year}-${String(month + 1).padStart(2, '0')}`;
    let bucket = buckets.get(key);
    if (!bucket) {
      bucket = {
        key,
        label: `${MONTHS[month]} ${year}`,
        sortValue: new Date(year, month, 1).getTime(),
        items: [],
      };
      buckets.set(key, bucket);
    }
    bucket.items.push(item);
  }

  const ordered = [...buckets.values()].sort((a, b) => b.sortValue - a.sortValue);
  // Sort items within a bucket newest-first too.
  for (const b of ordered) {
    b.items.sort((a, c) => (c.timestamp ?? 0) - (a.timestamp ?? 0));
  }

  if (undated.length > 0) {
    ordered.push({ key: 'undated', label: 'Undated', sortValue: -Infinity, items: undated });
  }
  return ordered;
}
